import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { useSelector } from 'react-redux';
import {
  Home,
  Wallet,
  History,
  FileText,
  Settings,
  Code2,
  ChevronLeft,
  ChevronRight,
  Users,
  CreditCard,
  Send,
  BookOpen,
  WalletCards,
  Globe,
  KeyRound,
  Repeat,
  BarChart3,
  Landmark,
  Scale,
  ScrollText,
  FileBarChart,
  BookUser,
  SlidersHorizontal,
} from 'lucide-react';

// Primary group — money & activity (matches redesign reference: Overview / Balance / ...)
const primaryItems = [
  { path: '/dashboard', icon: Home, label: 'Overview' },
  { path: '/dashboard/balance', icon: Wallet, label: 'Balance' },
  { path: '/dashboard/transactions', icon: History, label: 'Transactions' },
  { path: '/dashboard/invoices', icon: FileText, label: 'Invoices' },
  { path: '/dashboard/payouts', icon: Send, label: 'Payouts', merchantOnly: true },
  { path: '/dashboard/auto-conversion', icon: Repeat, label: 'Auto-conversion' },
];

// PREMIUM group — plan-gated features (page itself enforces the gate).
const premiumItems = [
  { path: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
];

// CONFIGURE group — settings & integration
const configureItems = [
  { path: '/dashboard/networks', icon: Globe, label: 'Networks & Tokens' },
  { path: '/dashboard/developer', icon: Code2, label: 'Developers' },
  { path: '/dashboard/billing', icon: CreditCard, label: 'Billing' },
  { path: '/dashboard/settings', icon: Settings, label: 'Settings' },
  { path: '/dashboard/guide', icon: BookOpen, label: 'Guide' },
];

const superAdminMenuItems = [
  { path: '/dashboard/merchants', icon: Users, label: 'Merchants' },
  { path: '/dashboard/payment-links', icon: KeyRound, label: 'API Keys' },
  { path: '/dashboard/payout-requests', icon: CreditCard, label: 'Payout Requests' },
  { path: '/dashboard/wallet-change-requests', icon: WalletCards, label: 'Wallet Requests' },
];

// OPERATIONS group — super-admin payout ops, treasury, logs & reporting
const superAdminOpsItems = [
  { path: '/dashboard/admin/treasury', icon: Landmark, label: 'Treasury' },
  { path: '/dashboard/admin/solvency', icon: Scale, label: 'Solvency' },
  { path: '/dashboard/admin/logs', icon: ScrollText, label: 'Logs' },
  { path: '/dashboard/admin/reports', icon: FileBarChart, label: 'Reports' },
  { path: '/dashboard/admin/address-book', icon: BookUser, label: 'Address Book' },
  { path: '/dashboard/admin/rules', icon: SlidersHorizontal, label: 'Rules Engine' },
];

const Sidebar = ({ collapsed, onToggle, drawerOpen = false, onClose }) => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;
  const location = useLocation();

  const visible = (items) => items.filter((i) => !i.merchantOnly || !isSuperAdmin);
  const primary = visible(primaryItems);
  const configure = visible(configureItems);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    if (drawerOpen && onClose) {
      onClose();
    }
  }, [location.pathname]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  // ── Sliding active indicator ───────────────────────────────────────────
  //
  // The active state used to be painted on each .nav-item, so moving between
  // sections snapped: one item's background vanished and another's appeared.
  // Instead a single element is measured onto the active link and animated,
  // which is what produces the slide.
  //
  // Measurement rather than CSS: the items sit in one <nav> with group labels
  // ("PREMIUM", "CONFIGURE") interleaved at arbitrary positions, and the list
  // changes with role — so the offset of the active item cannot be expressed
  // as a static rule. offsetTop/offsetHeight against the nav is exact for any
  // arrangement.
  const navRef = useRef(null);
  const [slider, setSlider] = useState(null);
  // No transition on the first placement: the indicator should already be on
  // the active item at paint, not fly in from the top of the nav.
  const [primed, setPrimed] = useState(false);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    const measure = () => {
      const active = nav.querySelector('.nav-item.active');
      // A route that is not in the sidebar (a detail page, say) leaves nothing
      // active — hide the indicator rather than stranding it on a stale item.
      if (!active) {
        setSlider(null);
        return;
      }
      const next = { top: active.offsetTop, height: active.offsetHeight };
      // Bail when the geometry has not moved. The ResizeObserver below fires on
      // any nav resize, and setting a fresh object every time would re-render
      // for nothing.
      setSlider((prev) =>
        prev && prev.top === next.top && prev.height === next.height ? prev : next,
      );
    };

    measure();
    // Two frames: one for the class to land, one for layout to settle after the
    // collapse width transition, so the indicator does not measure mid-animation.
    const raf = requestAnimationFrame(() => {
      measure();
      setPrimed(true);
    });

    // Heights shift when the sidebar collapses or the viewport changes, and the
    // item list itself changes with role — re-measure instead of assuming.
    const ro = new ResizeObserver(measure);
    ro.observe(nav);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [location.pathname, collapsed, drawerOpen]);

  return (
    <>
      <div
        className={`drawer-overlay ${drawerOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${drawerOpen ? 'drawer-open' : ''}`}>
      <div className="sidebar-header">
        <div className="logo">
          {!collapsed && (
            <>
            <Link to="/dashboard">
              <img src="/images/maxelpay-Logo.svg" alt="MaxelPay" className="desktop_lg"/>
              <img src="/images/maxelpay-Logo.svg" alt="MaxelPay" className="mobile_xl"/>
            </Link>
              {/* <div className="logo-icon">M</div>
              <span className="logo-text">MaxelPay</span> */}
            </>
          )}
          {collapsed && <div className="logo-icon">
            <Link to="/dashboard"><img src="/images/maxelpay-white.png" alt="MaxelPay" /></Link>
          </div>}
        </div>
        <button className="collapse-btn hide-logoBtn" onClick={onToggle}>
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <nav className="sidebar-nav" ref={navRef}>
        {/* Purely decorative: the accessible active state is still NavLink's
            aria-current on the item itself. */}
        {slider && (
          <span
            className={`nav-slider ${primed ? 'nav-slider--primed' : ''}`}
            style={{ transform: `translateY(${slider.top}px)`, height: slider.height }}
            aria-hidden="true"
          />
        )}
        {primary.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/dashboard'}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon size={20} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && <div className="nav-group-label">Premium</div>}
        {premiumItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon size={20} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && <div className="nav-group-label">Configure</div>}
        {configure.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon size={20} />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}

        {isSuperAdmin && (
          <>
            {!collapsed && <div className="nav-group-label">Admin</div>}
            {superAdminMenuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon size={20} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
            {!collapsed && <div className="nav-group-label">Operations</div>}
            {superAdminOpsItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon size={20} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        
 <p>
  <span className="need-help">
    Need Help?<br />
  Email us:
  </span>
   <span>
    <NavLink
  to="mailto:support@maxelpay.com"
  onClick={() => window.open("https://mail.google.com/mail/?view=cm&fs=1&to=support@maxelpay.com", "_blank")}
  className="support-icon"
>
    support@maxelpay.com
    </NavLink>
    
    </span>
</p>


  
        {!collapsed && (
          <div className="version-info">
            <br />
            <span>Version 1.0.0</span>
          </div>
        )}
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
